import config from "appConfig";

const theory = `<h3 class=sectionHead>Running convolution</h3>
<p>
   In FFT-based filtering, a continuous audio stream is filtered with a finite impulse response of length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   . Here, the signal is provided in blocks of samples (see <a href="#" data-img-src="${config.ASSETS_URL}/03/19/theory/figure-1.svg" data-img-alt="Figure 1. Blocks of an audio stream (continuous audio signal).">Figure 1</a>). All blocks have a fixed length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   , which is initially adjusted with respect to the admissible latency. Typical values are 64, 128 or 256 samples. A real-time filter processes the signal block by block. For each length-
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   input block
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   it computes a length-
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   output block
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>y</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . The introduced FFT technique computes a convolution in one step and requires that both operands (signal and filter) are available at entire length. Hence, it cannot be directly used for real-time filtering. It needs to be adapted, so that the convolution can be realized block by block as well. Therefore, two well-known principles can be used: The Overlap-Add (OLA) or the Overlap-Save (OLS) technique. Both are not restricted to the FFT approach and must be understood as an add-on to a conventional fast convolution [2].
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/19/theory/figure-1.svg" alt="Figure 1. Blocks of an audio stream (continuous audio signal)."><figcaption class=caption>Figure 1. Blocks of an audio stream (continuous audio signal).</figcaption></figure>
<h3 class=sectionHead>Overlap-Add convolution</h3>
<p>
   This approach convolves each block
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   of the signal with the filter of length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   . This yields intermediate results of lengths
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   , which is longer than
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   . The exceeding
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   samples are accumulated in a buffer and added to later output blocks
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>y</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mo class=MathClass-rel><</mo>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . <a href="#" data-img-src="${config.ASSETS_URL}/03/19/theory/figure-3.svg" data-img-alt="Figure 3. Block diagram of Overlap-Save (OLS) convolution using the FFT.">Figure 3</a> illustrates the Overlap-Add (OLA) technique in conjunction with FFT convolution. To summarize it, The OLA technique takes the input blocks as they are and assembles the output from the intermediary convolution results. As shown in the <a href="#" data-img-src="${config.ASSETS_URL}/03/19/theory/figure-2.svg" data-img-alt="Figure 2. Block diagram of Overlap-Add (OLA) convolution using the FFT.">Figure 2</a>, the Filter with length N and the audio block with B samples are both zero-padded transformed into frequency domain by FFT, then they are multiplied and converted back to time domain by using IFFT. The result will be added to the cyclic buffer as shown in the <a href="#" data-img-src="${config.ASSETS_URL}/03/19/theory/figure-2.svg" data-img-alt="Figure 2. Block diagram of Overlap-Add (OLA) convolution using the FFT.">Figure 2</a>. Further information can be found in [1].
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/19/theory/figure-2.svg" alt="Figure 2. Block diagram of Overlap-Add (OLA) convolution using the FFT."><figcaption class=caption>Figure 2. Block diagram of Overlap-Add (OLA) convolution using the FFT.</figcaption></figure>
<h3 class=sectionHead>Overlap-Save convolution</h3>
<p>
   The Overlap-Save (OLS) technique works exactly the other way around. Here, the input to the intermediary convolutions is prepared in a way that each convolution delivers a ready output block. The technique incorporates time-aliasing but directs it into ranges where it does not invalidate the convolution. It is depicted in <a href="#" data-img-src="${config.ASSETS_URL}/03/19/theory/figure-3.svg" data-img-alt="Figure 3. Block diagram of Overlap-Save (OLS) convolution using the FFT.">Figure 3</a> and works as follows. The
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>K</mi>
   </math>
   last input samples (which is known as sliding window) are convolved with the filter of length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   . This yields a result of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>K</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   samples. As the convolution is realized using a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>K</mi>
   </math>
   -point FFT, which implies that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
      <mo class=MathClass-bin>+</mo>
      <mn>1</mn>
   </math>
   samples wrap around the period and consequently corrupt
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
      <mo class=MathClass-bin>+</mo>
      <mn>1</mn>
   </math>
   samples (which is shown as marked area in <a href="#" data-img-src="${config.ASSETS_URL}/03/19/theory/figure-3.svg" data-img-alt="Figure 3. Block diagram of Overlap-Save (OLS) convolution using the FFT.">Figure 3</a>). The technique is called Overlap-Save, as these overlapping samples are discarded or in other words “saved”. The other samples mark a valid output block and they already contain the turn-off transients (which are the ‘decay’ from earlier input samples) of the preceding samples.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/19/theory/figure-3.svg" alt="Figure 3. Block diagram of Overlap-Save (OLS) convolution using the FFT."><figcaption class=caption>Figure 3. Block diagram of Overlap-Save (OLS) convolution using the FFT.</figcaption></figure>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>A. Papoulis. Signal Analysis. McGraw-Hill, New York, 1981.
   <dt>[2]
   <dd>F. Wefers and M. Vorl ̈ander. Flexible data structures for dynamic virtual auditory scenes. Virtual Reality, 22(4):281–295, 2018.
</dl>`;

export default theory;
